import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Calendar, MapPin, Plus, Loader2, Pencil, Trash2, Clock, DoorOpen,
  Check, X, Copy, Ticket, ChevronUp, ChevronDown, Printer, Map as MapIcon, Upload, FileText, ClipboardCheck, QrCode,
} from "lucide-react";
import GlobalEventAttendanceModal from "@/components/GlobalEventAttendanceModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/lib/toastBanner";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import EventAttendeesCard from "@/components/EventAttendeesCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "";
  try {
    const [y, m, day] = d.split("-").map((s) => parseInt(s, 10));
    if (!y || !m || !day) return d;
    return new Date(y, m - 1, day).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch { return d; }
}

function formatTime(t) {
  if (!t) return "";
  try {
    const [h, m] = t.split(":");
    const hh = parseInt(h, 10);
    if (isNaN(hh)) return t;
    const period = hh >= 12 ? "PM" : "AM";
    const display = ((hh + 11) % 12) + 1;
    return `${display}:${m || "00"} ${period}`;
  } catch { return t; }
}

const emptySessionDraft = { name: "", start_time: "", end_time: "", location: "" };

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [passports, setPassports] = useState([]);

  // Event
  const [eventEditOpen, setEventEditOpen] = useState(false);
  const [eventForm, setEventForm] = useState({ name: "", date: "", start_time: "", location: "" });
  const [eventSaving, setEventSaving] = useState(false);
  const [eventDeleteOpen, setEventDeleteOpen] = useState(false);
  const [eventDuplicating, setEventDuplicating] = useState(false);
  const [mapUploading, setMapUploading] = useState(false);
  const [mapClearing, setMapClearing] = useState(false);
  const [attendanceOpen, setAttendanceOpen] = useState(false);

  // Sessions
  const [newSession, setNewSession] = useState(emptySessionDraft);
  const [addingSession, setAddingSession] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [sessionDraft, setSessionDraft] = useState(emptySessionDraft);
  const [savingSession, setSavingSession] = useState(false);
  const [sessionDeleteTarget, setSessionDeleteTarget] = useState(null);
  const [duplicatingSessionId, setDuplicatingSessionId] = useState(null);

  // Passports
  const [passportModalOpen, setPassportModalOpen] = useState(false);
  const [editingPassportId, setEditingPassportId] = useState(null);
  // selectedSessionIds: ordered list. roomBySession: {sessionId: room}
  const [passportForm, setPassportForm] = useState({ name: "", selectedSessionIds: [], roomBySession: {} });
  const [passportSaving, setPassportSaving] = useState(false);
  const [duplicatingPassportId, setDuplicatingPassportId] = useState(null);
  const [passportDeleteTarget, setPassportDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/events/${id}`, { withCredentials: true });
      setEvent(data?.event || null);
      setSessions(data?.sessions || []);
      setPassports(data?.passports || []);
    } catch (err) {
      toast.error(err?.response?.status === 404 ? "Event not found" : "Failed to load event");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // ── Event ────────────────────────────────────────────────────────
  const openEventEdit = () => {
    if (!event) return;
    setEventForm({
      name: event.name || "",
      date: event.date || "",
      start_time: event.start_time || "",
      location: event.location || "",
    });
    setEventEditOpen(true);
  };

  const saveEvent = async () => {
    if (!eventForm.name.trim()) { toast.error("Event name required"); return; }
    if (!eventForm.date) { toast.error("Event date required"); return; }
    if (!eventForm.start_time) { toast.error("Event start time required"); return; }
    setEventSaving(true);
    try {
      const { data } = await axios.put(`${API}/events/${id}`, {
        name: eventForm.name.trim(),
        date: eventForm.date,
        start_time: eventForm.start_time,
        location: eventForm.location.trim(),
      }, { withCredentials: true });
      setEvent(data);
      toast.success("Event updated");
      setEventEditOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update event");
    } finally { setEventSaving(false); }
  };

  const deleteEvent = async () => {
    try {
      await axios.delete(`${API}/events/${id}`, { withCredentials: true });
      toast.success("Event deleted");
      navigate("/admin/events");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete event");
    }
  };

  const duplicateEvent = async () => {
    setEventDuplicating(true);
    try {
      const { data } = await axios.post(`${API}/events/${id}/duplicate`, {}, { withCredentials: true });
      toast.success("Event duplicated");
      navigate(`/admin/events/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to duplicate event");
    } finally { setEventDuplicating(false); }
  };

  const handleMapUpload = async (file) => {
    if (!file) return;
    const name = file.name || "";
    const ext = name.toLowerCase().split(".").pop();
    if (!["pdf", "jpg", "jpeg"].includes(ext)) {
      toast.error("Map must be a PDF or JPG file");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large (max 25 MB)");
      return;
    }
    setMapUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/events/${id}/map`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEvent((prev) => ({ ...prev, map_url: data.map_url, map_filename: data.map_filename }));
      toast.success("Map uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload map");
    } finally { setMapUploading(false); }
  };

  const handleMapClear = async () => {
    setMapClearing(true);
    try {
      await axios.delete(`${API}/events/${id}/map`, { withCredentials: true });
      setEvent((prev) => ({ ...prev, map_url: "", map_filename: "" }));
      toast.success("Map removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove map");
    } finally { setMapClearing(false); }
  };

  const mapHref = (event?.map_url || "").startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${event.map_url}`
    : (event?.map_url || "");

  // ── Session ──────────────────────────────────────────────────────
  const validateSession = (draft) => {
    if (!draft.name.trim()) { toast.error("Session name required"); return false; }
    if (!draft.start_time) { toast.error("Session start time required"); return false; }
    if (!draft.end_time) { toast.error("Session end time required"); return false; }
    if (draft.end_time <= draft.start_time) { toast.error("End time must be after start time"); return false; }
    return true;
  };

  const addSession = async () => {
    if (!validateSession(newSession)) return;
    setAddingSession(true);
    try {
      const { data } = await axios.post(`${API}/events/${id}/sessions`, {
        name: newSession.name.trim(),
        start_time: newSession.start_time,
        end_time: newSession.end_time,
        location: newSession.location.trim(),
      }, { withCredentials: true });
      setSessions((prev) => [...prev, data].sort((a, b) => a.start_time.localeCompare(b.start_time)));
      setNewSession(emptySessionDraft);
      toast.success("Session added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add session");
    } finally { setAddingSession(false); }
  };

  const beginEditSession = (s) => {
    setEditingSessionId(s.id);
    setSessionDraft({ name: s.name, start_time: s.start_time, end_time: s.end_time, location: s.location || "" });
  };

  const cancelEditSession = () => { setEditingSessionId(null); setSessionDraft(emptySessionDraft); };

  const saveEditSession = async () => {
    if (!validateSession(sessionDraft)) return;
    setSavingSession(true);
    try {
      const { data } = await axios.put(`${API}/events/${id}/sessions/${editingSessionId}`, {
        name: sessionDraft.name.trim(),
        start_time: sessionDraft.start_time,
        end_time: sessionDraft.end_time,
        location: sessionDraft.location.trim(),
      }, { withCredentials: true });
      setSessions((prev) => prev.map((s) => (s.id === editingSessionId ? data : s)).sort((a, b) => a.start_time.localeCompare(b.start_time)));
      cancelEditSession();
      toast.success("Session updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update session");
    } finally { setSavingSession(false); }
  };

  const deleteSession = async () => {
    const s = sessionDeleteTarget;
    if (!s) return;
    try {
      await axios.delete(`${API}/events/${id}/sessions/${s.id}`, { withCredentials: true });
      setSessions((prev) => prev.filter((x) => x.id !== s.id));
      setPassports((prev) => prev.map((p) => ({ ...p, assignments: (p.assignments || []).filter((a) => a.session_id !== s.id) })));
      toast.success("Session deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete session");
    } finally { setSessionDeleteTarget(null); }
  };

  const duplicateSession = async (s) => {
    setDuplicatingSessionId(s.id);
    try {
      const { data } = await axios.post(`${API}/events/${id}/sessions/${s.id}/duplicate`, {}, { withCredentials: true });
      setSessions((prev) => [...prev, data].sort((a, b) => a.start_time.localeCompare(b.start_time)));
      toast.success("Session duplicated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to duplicate session");
    } finally { setDuplicatingSessionId(null); }
  };

  // ── Passport ─────────────────────────────────────────────────────
  const openPassportCreate = () => {
    setEditingPassportId(null);
    setPassportForm({ name: "", selectedSessionIds: [], roomBySession: {} });
    setPassportModalOpen(true);
  };

  const openPassportEdit = (p) => {
    const selectedSessionIds = [];
    const roomBySession = {};
    (p.assignments || []).forEach((a) => {
      if (sessions.find((s) => s.id === a.session_id)) {
        selectedSessionIds.push(a.session_id);
        roomBySession[a.session_id] = a.room || "";
      }
    });
    setEditingPassportId(p.id);
    setPassportForm({ name: p.name || "", selectedSessionIds, roomBySession });
    setPassportModalOpen(true);
  };

  const toggleSessionSelected = (sid) => {
    setPassportForm((prev) => {
      const isSelected = prev.selectedSessionIds.includes(sid);
      if (isSelected) {
        return { ...prev, selectedSessionIds: prev.selectedSessionIds.filter((x) => x !== sid) };
      }
      return { ...prev, selectedSessionIds: [...prev.selectedSessionIds, sid] };
    });
  };

  const moveSession = (sid, direction) => {
    setPassportForm((prev) => {
      const idx = prev.selectedSessionIds.indexOf(sid);
      if (idx < 0) return prev;
      const target = direction === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.selectedSessionIds.length) return prev;
      const next = [...prev.selectedSessionIds];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...prev, selectedSessionIds: next };
    });
  };

  const setRoomFor = (sid, room) => {
    setPassportForm((prev) => ({ ...prev, roomBySession: { ...prev.roomBySession, [sid]: room } }));
  };

  const savePassport = async () => {
    if (!passportForm.name.trim()) { toast.error("Passport name required"); return; }
    if (passportForm.selectedSessionIds.length === 0) { toast.error("Select at least one session"); return; }
    const assignments = passportForm.selectedSessionIds.map((sid) => ({
      session_id: sid,
      room: (passportForm.roomBySession[sid] || "").trim(),
    }));
    setPassportSaving(true);
    try {
      const body = { name: passportForm.name.trim(), assignments };
      if (editingPassportId) {
        const { data } = await axios.put(`${API}/events/${id}/passports/${editingPassportId}`, body, { withCredentials: true });
        setPassports((prev) => prev.map((p) => (p.id === editingPassportId ? data : p)).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        toast.success("Passport updated");
      } else {
        const { data } = await axios.post(`${API}/events/${id}/passports`, body, { withCredentials: true });
        setPassports((prev) => [...prev, data].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        toast.success("Passport created");
      }
      setPassportModalOpen(false);
      setEditingPassportId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save passport");
    } finally { setPassportSaving(false); }
  };

  const duplicatePassport = async (p) => {
    setDuplicatingPassportId(p.id);
    try {
      const { data } = await axios.post(`${API}/events/${id}/passports/${p.id}/duplicate`, {}, { withCredentials: true });
      setPassports((prev) => [...prev, data].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      toast.success("Passport duplicated");
      openPassportEdit(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to duplicate passport");
    } finally { setDuplicatingPassportId(null); }
  };

  const deletePassport = async () => {
    const p = passportDeleteTarget;
    if (!p) return;
    try {
      await axios.delete(`${API}/events/${id}/passports/${p.id}`, { withCredentials: true });
      setPassports((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Passport deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete passport");
    } finally { setPassportDeleteTarget(null); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#6E7787]" /></div>;
  }
  if (!event) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm font-body text-[#6E7787]">Event not found.</p>
        <Button variant="outline" onClick={() => navigate("/admin/events")} className="mt-4 gap-2"><ArrowLeft className="h-4 w-4" /> Back to Events</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="event-detail-page">
      <button
        onClick={() => navigate("/admin/events")}
        className="inline-flex items-center gap-1.5 text-xs font-body text-[#A1A9B8] hover:text-white transition-colors"
        data-testid="event-back-btn"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Events
      </button>

      {/* Event header */}
      <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-6 overflow-hidden" data-testid="event-header-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-2 mb-2">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "var(--primary-color, #4A90D9)" }}>
                <Calendar className="h-4 w-4 text-white" />
              </div>
              <span className="text-[10px] font-body uppercase tracking-wider text-[#6E7787]">Event</span>
            </div>
            <h1 className="font-heading text-lg sm:text-2xl font-bold text-white [overflow-wrap:anywhere] break-words" data-testid="event-name">{event.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm font-body text-[#A1A9B8] min-w-0">
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 shrink-0" /> {formatDate(event.date)}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 shrink-0" /> {formatTime(event.start_time)}</span>
              {event.location ? (
                <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full" data-testid="event-location-inline">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{event.location}</span>
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto sm:shrink-0" data-testid="event-header-actions">
            <Button variant="outline" size="sm" onClick={() => setAttendanceOpen(true)} className="gap-1.5" data-testid="event-attendance-btn">
              <ClipboardCheck className="h-3.5 w-3.5" /> Check In
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open(`/events/${id}/checkin`, "_blank", "noopener,noreferrer")} className="gap-1.5" data-testid="event-checkin-qr-btn">
              <QrCode className="h-3.5 w-3.5" /> Check In QR
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/admin/events/${id}/survey-print`)} className="gap-1.5" data-testid="event-survey-qr-btn">
              <QrCode className="h-3.5 w-3.5" /> Survey QR
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/admin/events/${id}/print`)} className="gap-1.5" data-testid="event-print-btn">
              <Printer className="h-3.5 w-3.5" /> Print
            </Button>
            <Button variant="outline" size="sm" onClick={openEventEdit} className="gap-1.5" data-testid="event-edit-btn">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
            <Button variant="outline" size="sm" onClick={duplicateEvent} disabled={eventDuplicating} className="gap-1.5" data-testid="event-duplicate-btn">
              {eventDuplicating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Copy className="h-3.5 w-3.5" /> Duplicate</>}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEventDeleteOpen(true)} className="gap-1.5 text-red-400 border-red-400/30 hover:bg-red-500/10" data-testid="event-delete-btn">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </Button>
          </div>
        </div>

        {/* Map upload row */}
        <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-3 flex-wrap" data-testid="event-map-row">
          <div className="min-w-0 flex-1">
            <p className="font-body text-sm font-semibold text-white inline-flex items-center gap-2">
              <MapIcon className="h-4 w-4 text-[#6E7787]" /> Venue map
            </p>
            <p className="text-xs font-body text-[#6E7787] mt-0.5">
              Upload a PDF or JPG map. The link appears on each learner's check-in itinerary.
            </p>
            {event.map_url ? (
              <a
                href={mapHref}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-2 text-sm font-body text-[var(--primary-color,#4A90D9)] hover:underline truncate max-w-full"
                data-testid="event-map-link"
              >
                <FileText className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{event.map_filename || "View map"}</span>
              </a>
            ) : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <label
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-white/15 text-xs font-body cursor-pointer transition-colors ${
                mapUploading ? "opacity-60 cursor-wait" : "hover:bg-white/5"
              }`}
              data-testid="event-map-upload-label"
            >
              {mapUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              <span>{event.map_url ? "Replace" : "Upload Map"}</span>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg"
                className="hidden"
                disabled={mapUploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleMapUpload(f);
                  e.target.value = "";
                }}
                data-testid="event-map-upload-input"
              />
            </label>
            {event.map_url ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMapClear}
                disabled={mapClearing}
                className="gap-1.5 text-red-400 border-red-400/30 hover:bg-red-500/10"
                data-testid="event-map-clear"
              >
                {mapClearing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Remove
              </Button>
            ) : null}
          </div>
        </div>

        {/* Passport quick-summary: time block + room(s) per passport so
            anyone reading the event details can see assignments at a glance
            without scrolling down to the Passports section. */}
        {passports.length > 0 ? (
          <div className="mt-6 pt-5 border-t border-white/5" data-testid="event-passport-summary">
            <div className="flex items-center gap-2 mb-3">
              <Ticket className="h-3.5 w-3.5 text-[var(--primary-color)]" />
              <span className="text-[10px] uppercase tracking-wider font-body font-semibold text-[#A1A9B8]">
                Passport Summary
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {passports.map((p) => {
                const items = (p.assignments || [])
                  .map((a) => ({ s: sessions.find((x) => x.id === a.session_id), room: (a.room || "").trim() }))
                  .filter((x) => x.s);
                if (items.length === 0) {
                  return (
                    <div
                      key={p.id}
                      className="rounded-lg border border-white bg-white px-3 py-2 flex items-center justify-between gap-2"
                      data-testid={`passport-summary-${p.id}`}
                    >
                      <span className="text-sm font-body font-semibold text-black truncate">{p.name}</span>
                      <span className="text-[11px] font-body text-black/60 shrink-0">No sessions</span>
                    </div>
                  );
                }
                const starts = items.map((x) => x.s.start_time).filter(Boolean).sort();
                const ends = items.map((x) => x.s.end_time).filter(Boolean).sort();
                const startTime = starts[0];
                const endTime = ends[ends.length - 1];
                const rooms = Array.from(new Set(items.map((x) => x.room).filter(Boolean)));
                return (
                  <div
                    key={p.id}
                    className="rounded-lg border border-white bg-white px-3 py-2"
                    data-testid={`passport-summary-${p.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-body font-semibold text-black truncate">{p.name}</span>
                      <span className="text-[10px] font-body text-black/60 tabular-nums shrink-0">
                        {items.length} session{items.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] font-body text-black">
                      {startTime && endTime ? (
                        <span className="inline-flex items-center gap-1 tabular-nums">
                          <Clock className="h-3 w-3 text-black/70" />
                          {formatTime(startTime)} – {formatTime(endTime)}
                        </span>
                      ) : null}
                      {rooms.length > 0 ? (
                        <span className="inline-flex items-center gap-1 min-w-0">
                          <DoorOpen className="h-3 w-3 text-black/70 shrink-0" />
                          <span className="truncate">{rooms.join(" · ")}</span>
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {/* Attendees — standalone event-only guest list */}
      <EventAttendeesCard eventId={id} passports={passports} />

      {/* Sessions */}
      <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-5" data-testid="event-sessions-card">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h2 className="font-heading text-lg font-semibold text-white inline-flex items-center gap-2"><Clock className="h-4 w-4 text-[#6E7787]" /> Sessions</h2>
            <p className="text-xs font-body text-[#6E7787] mt-0.5">Time blocks within the event. Location is optional and surfaces on the itinerary.</p>
          </div>
        </div>

        {sessions.length === 0 ? null : (
          <ul className="space-y-2 mb-3" data-testid="sessions-list">
            {sessions.map((s) => (
              <li key={s.id} className="rounded-lg border border-white/10 px-3 py-2.5 hover:border-white/20 transition-colors" data-testid={`session-row-${s.id}`}>
                {editingSessionId === s.id ? (
                  <div className="grid grid-cols-1 md:grid-cols-[1fr,130px,130px,1fr,auto] gap-2 items-center">
                    <Input value={sessionDraft.name} onChange={(e) => setSessionDraft({ ...sessionDraft, name: e.target.value })} placeholder="Session name" data-testid={`session-edit-name-${s.id}`} />
                    <Input type="time" value={sessionDraft.start_time} onChange={(e) => setSessionDraft({ ...sessionDraft, start_time: e.target.value })} data-testid={`session-edit-start-${s.id}`} />
                    <Input type="time" value={sessionDraft.end_time} onChange={(e) => setSessionDraft({ ...sessionDraft, end_time: e.target.value })} data-testid={`session-edit-end-${s.id}`} />
                    <Input value={sessionDraft.location} onChange={(e) => setSessionDraft({ ...sessionDraft, location: e.target.value })} placeholder="Location (optional)" data-testid={`session-edit-location-${s.id}`} />
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" onClick={saveEditSession} disabled={savingSession} className="gap-1.5" data-testid={`session-save-${s.id}`}>
                        {savingSession ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5" /> Save</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEditSession} disabled={savingSession}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-body text-sm font-medium text-white truncate">{s.name}</p>
                      <div className="text-xs font-body text-[#A1A9B8] mt-0.5 inline-flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" /> {formatTime(s.start_time)} – {formatTime(s.end_time)}</span>
                        {s.location ? (
                          <span className="inline-flex items-center gap-1.5" data-testid={`session-location-${s.id}`}><MapPin className="h-3 w-3" /> {s.location}</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => beginEditSession(s)} className="h-7 w-7 rounded flex items-center justify-center text-[#A1A9B8] hover:text-white hover:bg-white/5" title="Edit" data-testid={`session-edit-${s.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => duplicateSession(s)}
                        disabled={duplicatingSessionId === s.id}
                        className="h-7 w-7 rounded flex items-center justify-center text-[#A1A9B8] hover:text-white hover:bg-white/5 disabled:opacity-40"
                        title="Duplicate"
                        data-testid={`session-duplicate-${s.id}`}
                      >
                        {duplicatingSessionId === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                      <button onClick={() => setSessionDeleteTarget(s)} className="h-7 w-7 rounded flex items-center justify-center text-red-400 hover:bg-red-500/10" title="Delete" data-testid={`session-delete-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Inline add */}
        <div className="rounded-lg border border-dashed border-white/10 px-3 py-3" data-testid="session-add-row">
          <div className="grid grid-cols-1 md:grid-cols-[1fr,130px,130px,1fr,auto] gap-2 items-center">
            <Input
              value={newSession.name}
              onChange={(e) => setNewSession({ ...newSession, name: e.target.value })}
              placeholder="Session name"
              data-testid="session-new-name"
            />
            <Input type="time" value={newSession.start_time} onChange={(e) => setNewSession({ ...newSession, start_time: e.target.value })} data-testid="session-new-start" />
            <Input type="time" value={newSession.end_time} onChange={(e) => setNewSession({ ...newSession, end_time: e.target.value })} data-testid="session-new-end" />
            <Input
              value={newSession.location}
              onChange={(e) => setNewSession({ ...newSession, location: e.target.value })}
              placeholder="Location (optional)"
              data-testid="session-new-location"
            />
            <Button onClick={addSession} disabled={addingSession} className="gap-1.5" data-testid="session-add-submit">
              {addingSession ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5" /> Add Session</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Passports */}
      <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-5" data-testid="event-passports-card">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h2 className="font-heading text-lg font-semibold text-white inline-flex items-center gap-2"><Ticket className="h-4 w-4 text-[#6E7787]" /> Passports</h2>
            <p className="text-xs font-body text-[#6E7787] mt-0.5">A passport selects and orders sessions, with a room per session.</p>
          </div>
          <Button
            onClick={openPassportCreate}
            disabled={sessions.length === 0}
            className="gap-2"
            style={{ background: "var(--primary-color)", color: "var(--admin-button-fg, #fff)" }}
            data-testid="passport-create-btn"
          >
            <Plus className="h-4 w-4" /> New Passport
          </Button>
        </div>

        {sessions.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/10 px-6 py-8 text-center" data-testid="passports-need-sessions">
            <p className="text-sm font-body text-[#6E7787]">Add a session first — passports select and order this event's sessions.</p>
          </div>
        ) : passports.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/10 px-6 py-8 text-center" data-testid="passports-empty">
            <Ticket className="h-8 w-8 mx-auto text-[#6E7787]" />
            <p className="text-sm font-body text-[#6E7787] mt-2">No passports yet. Click <span className="text-white">New Passport</span> to build one.</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="passports-list">
            {passports.map((p) => (
              <li key={p.id} className="rounded-lg border border-white/10 px-4 py-3 hover:border-white/20 transition-colors" data-testid={`passport-row-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-body text-sm font-semibold text-white truncate">{p.name}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(p.assignments || []).length === 0 ? (
                        <span className="text-[11px] font-body text-[#6E7787]">No sessions selected</span>
                      ) : (
                        (p.assignments || []).map((a, idx) => {
                          const s = sessions.find((x) => x.id === a.session_id);
                          if (!s) return null;
                          const room = (a.room || "").trim();
                          return (
                            <span
                              key={a.session_id}
                              className="inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2 py-1 text-[11px] font-body bg-black/20"
                              data-testid={`passport-${p.id}-session-${a.session_id}`}
                            >
                              <span className="text-[10px] text-[#6E7787]">{idx + 1}.</span>
                              <span className="text-white truncate max-w-[160px]">{s.name}</span>
                              <span className="inline-flex items-center gap-1 text-[#A1A9B8]">
                                <Clock className="h-3 w-3" /> {formatTime(s.start_time)}{s.end_time ? `–${formatTime(s.end_time)}` : ""}
                              </span>
                              {room ? (
                                <span className="inline-flex items-center gap-1 text-[#A1A9B8]"><DoorOpen className="h-3 w-3" /> {room}</span>
                              ) : (
                                <span className="text-[#6E7787]">— no room</span>
                              )}
                            </span>
                          );
                        })
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => openPassportEdit(p)} className="h-7 w-7 rounded flex items-center justify-center text-[#A1A9B8] hover:text-white hover:bg-white/5" title="Edit" data-testid={`passport-edit-${p.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => duplicatePassport(p)}
                      disabled={duplicatingPassportId === p.id}
                      className="h-7 w-7 rounded flex items-center justify-center text-[#A1A9B8] hover:text-white hover:bg-white/5 disabled:opacity-40"
                      title="Duplicate"
                      data-testid={`passport-duplicate-${p.id}`}
                    >
                      {duplicatingPassportId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                    <button onClick={() => setPassportDeleteTarget(p)} className="h-7 w-7 rounded flex items-center justify-center text-red-400 hover:bg-red-500/10" title="Delete" data-testid={`passport-delete-${p.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Event edit modal */}
      <Dialog open={eventEditOpen} onOpenChange={(v) => { if (!eventSaving) setEventEditOpen(v); }}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white" data-testid="event-edit-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-lg text-white">Edit Event</DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body text-sm">Update the event details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-body text-[#A1A9B8]">Event name <span className="text-red-400">*</span></Label>
              <Input value={eventForm.name} onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })} data-testid="event-edit-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-body text-[#A1A9B8]">Date <span className="text-red-400">*</span></Label>
                <Input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} data-testid="event-edit-date" />
              </div>
              <div>
                <Label className="text-xs font-body text-[#A1A9B8]">Start time <span className="text-red-400">*</span></Label>
                <Input type="time" value={eventForm.start_time} onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })} data-testid="event-edit-start-time" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-body text-[#A1A9B8] inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Location <span className="text-[#6E7787]">(optional)</span></Label>
              <Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} data-testid="event-edit-location" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEventEditOpen(false)} disabled={eventSaving}>Cancel</Button>
            <Button onClick={saveEvent} disabled={eventSaving} data-testid="event-edit-save">
              {eventSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={eventDeleteOpen}
        onOpenChange={setEventDeleteOpen}
        onConfirm={deleteEvent}
        title="Delete this event?"
        description={`"${event.name}" and all its sessions and passports will be permanently removed.`}
      />

      <DeleteConfirmDialog
        open={!!sessionDeleteTarget}
        onOpenChange={(v) => !v && setSessionDeleteTarget(null)}
        onConfirm={deleteSession}
        title="Delete this session?"
        description={sessionDeleteTarget ? `"${sessionDeleteTarget.name}" will be removed from this event and from any passports that reference it.` : ""}
      />

      {/* Passport modal — light mode */}
      <Dialog open={passportModalOpen} onOpenChange={(v) => { if (!passportSaving) { setPassportModalOpen(v); if (!v) setEditingPassportId(null); } }}>
        <DialogContent className="bg-white text-slate-900 border-slate-200 max-w-2xl" data-testid="passport-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-lg text-slate-900">{editingPassportId ? "Edit Passport" : "New Passport"}</DialogTitle>
            <DialogDescription className="text-slate-500 font-body text-sm">Name the passport, choose its sessions, and order them. Add a room per session (optional).</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <Label className="text-xs font-body text-slate-600">Passport name <span className="text-red-500">*</span></Label>
              <Input
                value={passportForm.name}
                onChange={(e) => setPassportForm({ ...passportForm, name: e.target.value })}
                placeholder="Track A — Cardiology"
                autoFocus
                className="bg-white text-slate-900 border-slate-300 placeholder:text-slate-400 focus-visible:ring-slate-400"
                data-testid="passport-name-input"
              />
            </div>

            <div>
              <Label className="text-xs font-body text-slate-600">Sessions <span className="text-red-500">*</span></Label>
              <p className="text-[11px] font-body text-slate-500 mt-0.5">Check sessions to include them; reorder with the arrows. The order is the visit order.</p>
              <div className="mt-2 rounded-lg border border-slate-200 divide-y divide-slate-200 max-h-[44vh] overflow-y-auto">
                {/* Selected sessions in order */}
                {passportForm.selectedSessionIds.map((sid, idx) => {
                  const s = sessions.find((x) => x.id === sid);
                  if (!s) return null;
                  return (
                    <div key={sid} className="px-3 py-2.5 flex items-center gap-3 bg-slate-50" data-testid={`passport-selected-${sid}`}>
                      <Checkbox
                        checked={true}
                        onCheckedChange={() => toggleSessionSelected(sid)}
                        className="border-slate-400 data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900"
                        data-testid={`passport-checkbox-${sid}`}
                      />
                      <span className="text-[11px] font-mono text-slate-500 w-5 text-right">{idx + 1}.</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-body font-medium text-slate-900 truncate">{s.name}</p>
                        <p className="text-[11px] font-body text-slate-500 inline-flex items-center gap-1.5">
                          <Clock className="h-3 w-3" /> {formatTime(s.start_time)} – {formatTime(s.end_time)}
                          {s.location ? <><MapPin className="h-3 w-3 ml-1.5" /> {s.location}</> : null}
                        </p>
                      </div>
                      <Input
                        value={passportForm.roomBySession[sid] || ""}
                        onChange={(e) => setRoomFor(sid, e.target.value)}
                        placeholder="Room"
                        className="w-32 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400 focus-visible:ring-slate-400 h-8 text-sm"
                        data-testid={`passport-room-input-${sid}`}
                      />
                      <div className="flex flex-col">
                        <button
                          onClick={() => moveSession(sid, "up")}
                          disabled={idx === 0}
                          className="h-4 w-6 flex items-center justify-center text-slate-500 hover:text-slate-900 disabled:opacity-30"
                          title="Move up"
                          data-testid={`passport-move-up-${sid}`}
                        >
                          <ChevronUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => moveSession(sid, "down")}
                          disabled={idx === passportForm.selectedSessionIds.length - 1}
                          className="h-4 w-6 flex items-center justify-center text-slate-500 hover:text-slate-900 disabled:opacity-30"
                          title="Move down"
                          data-testid={`passport-move-down-${sid}`}
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {/* Unselected sessions */}
                {sessions
                  .filter((s) => !passportForm.selectedSessionIds.includes(s.id))
                  .map((s) => (
                    <div key={s.id} className="px-3 py-2.5 flex items-center gap-3 bg-white" data-testid={`passport-available-${s.id}`}>
                      <Checkbox
                        checked={false}
                        onCheckedChange={() => toggleSessionSelected(s.id)}
                        className="border-slate-400 data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900"
                        data-testid={`passport-checkbox-${s.id}`}
                      />
                      <span className="w-5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-body font-medium text-slate-700 truncate">{s.name}</p>
                        <p className="text-[11px] font-body text-slate-500 inline-flex items-center gap-1.5">
                          <Clock className="h-3 w-3" /> {formatTime(s.start_time)} – {formatTime(s.end_time)}
                          {s.location ? <><MapPin className="h-3 w-3 ml-1.5" /> {s.location}</> : null}
                        </p>
                      </div>
                    </div>
                  ))}
                {sessions.length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-slate-500">No sessions to choose from yet.</div>
                ) : null}
              </div>
              <p className="text-[11px] font-body text-slate-500 mt-2" data-testid="passport-selection-summary">
                {passportForm.selectedSessionIds.length} of {sessions.length} session{sessions.length === 1 ? "" : "s"} selected
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setPassportModalOpen(false); setEditingPassportId(null); }}
              disabled={passportSaving}
              className="bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900"
            >
              Cancel
            </Button>
            <Button
              onClick={savePassport}
              disabled={passportSaving}
              className="gap-1.5 bg-slate-900 text-white hover:bg-slate-800"
              data-testid="passport-save-btn"
            >
              {passportSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-3.5 w-3.5" /> {editingPassportId ? "Save Changes" : "Create Passport"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!passportDeleteTarget}
        onOpenChange={(v) => !v && setPassportDeleteTarget(null)}
        onConfirm={deletePassport}
        title="Delete this passport?"
        description={passportDeleteTarget ? `"${passportDeleteTarget.name}" will be permanently removed.` : ""}
      />

      <GlobalEventAttendanceModal
        open={attendanceOpen}
        onClose={() => setAttendanceOpen(false)}
        event={event}
      />

    </div>
  );
}
